import { BotData, BotType, ClientData } from "@shared/types";
import { BotFormType } from "@shared/features/bots/BotForm/common/types";
import { useContext } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { GroupOverviewContext } from "@shared/features/onboarding/GroupOverview.context";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { MAX_FETCH_ALL_ACCOUNT_AMOUNTS } from "@shared/features/bots/BotForm/common/constants";
import useMainCoinsAvailableAmounts from "@shared/utils/hooks/useMainCoinsAvailableAmounts";
import { skipToken } from "@reduxjs/toolkit/query";
import { useClientsData } from "@shared/utils/hooks/misc";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";

interface useBotFormAccountAmountsProps {
  botType?: BotType;
  botFormType?: BotFormType;
  account?: Pick<ClientData, "client_id" | "exchange">;
  symbol?: string;
  botsData?: BotData[];
  skip?: boolean;
}

interface GetBotFormAccountAmountsArgsParams {
  account?: Pick<ClientData, "client_id">;
  accountIds?: number[];
  shouldFetchAllAccountsAmounts: boolean;
  shouldReturnEffectiveTotal: boolean;
}

export const getBotFormAccountAmountsArgs = ({
  account,
  accountIds,
  shouldFetchAllAccountsAmounts,
  shouldReturnEffectiveTotal,
}: GetBotFormAccountAmountsArgsParams) => {
  const withEffectiveTotal = <T extends Record<string, unknown>>(args?: T) => {
    return shouldReturnEffectiveTotal
      ? { ...args, return_effective_total: true }
      : args;
  };

  if (accountIds) return withEffectiveTotal({ client_ids: accountIds });
  else if (shouldFetchAllAccountsAmounts) return withEffectiveTotal(undefined);
  return account
    ? withEffectiveTotal({ client_id: account.client_id })
    : skipToken;
};

export const useBotFormAccountAmounts = ({
  botFormType,
  account,
  botsData,
  botType: propsBotType,
  symbol,
  skip: propsSkip,
}: useBotFormAccountAmountsProps) => {
  const { botType: formBotType, isChannelConfiguration } =
    useContext(BotFormContext) || {};
  const { isSignal } = useContext(TradingTerminalContext) || {};
  const isGroupOverview = !!useContext(GroupOverviewContext);
  const isOnboarding = !!useContext(OnboardingContext);
  const botType = propsBotType ?? formBotType;
  const { activeClientsData, getClientsListStatus } = useClientsData();
  const isSignalsBot = !botType || botType === BotType.SIGNAL;
  const isBulkEdit = botFormType === BotFormType.BULK_EDIT;
  const isBulkCreate = botFormType === BotFormType.BULK_CREATE;
  const accountIds = botsData?.map((bot) => bot.client_id);
  const shouldFetchAllAccountsAmounts =
    isGroupOverview ||
    isBulkCreate ||
    (!!activeClientsData &&
      activeClientsData.length <= MAX_FETCH_ALL_ACCOUNT_AMOUNTS);
  const shouldReturnEffectiveTotal =
    !!botFormType && !!botType && botType !== BotType.SIGNAL;

  const getSkipAccountAmounts = () => {
    if (botType === BotType.TRADING_VIEW_SIGNALS) return true;
    if (isBulkEdit) return !accountIds?.length;
    return (
      isChannelConfiguration ||
      !getClientsListStatus.isSuccess ||
      !activeClientsData?.length ||
      (!shouldFetchAllAccountsAmounts && !account)
    );
  };

  const getAccountsArgs = () => {
    return getBotFormAccountAmountsArgs({
      account,
      accountIds,
      shouldFetchAllAccountsAmounts,
      shouldReturnEffectiveTotal,
    });
  };

  const skip = propsSkip || getSkipAccountAmounts() || isSignal;

  return useMainCoinsAvailableAmounts({
    symbol,
    exchange: account?.exchange,
    accountId: account?.client_id,
    accountsArgs: getAccountsArgs(),
    withTotal: isSignalsBot || isOnboarding,
    skip,
  });
};
